import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Archive, ChevronDown, Download, FileDown, Mail, Plus, Search, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Shell } from "../components/Shell";
import { CopyField } from "../components/CopyField";
import { EditDialog } from "../components/EditDialog";
import { ResetPasswordButton } from "../components/ResetPasswordButton";
import { CompanyProfile } from "../components/CompanyProfile";
import { ArchiveTab } from "../components/ArchiveTab";
import { UsageMeter } from "../components/UsageMeter";
import { TeamUsersTab } from "../components/TeamUsersTab";
import { NotificationBell } from "../components/NotificationBell";
import { useAuth } from "../context/AuthContext";
import { UpgradeToCompanyButton } from "../components/UpgradeToCompanyButton";
import { CreditApplicationView } from "../components/CreditApplicationView";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const QUESTIONS = [
  ["phone", "Reference phone number"],
  ["account_duration", "Length of active account"],
  ["terms_extended", "Terms extended"],
  ["highest_balance", "Highest balance granted"],
  ["current_balance", "Current outstanding balance"],
  ["past_due", "Past-due amounts"],
  ["registry_opt_in", "Reference registry opt-in"],
  ["comments", "Additional comments"],
];

export default function CompanyAdmin() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("company_admin");
  const [company, setCompany] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [refs, setRefs] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [open, setOpen] = useState(false);
  const [archiveKey, setArchiveKey] = useState(0);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("customers");
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    federal_tax_id: "",
  });
  const [requests, setRequests] = useState([]);

  const loadRequests = useCallback(async () => {
    const { data } = await api.get("/company/access-requests");
    setRequests(data);
  }, []);

  const load = useCallback(async () => {
    const [c, cu] = await Promise.all([api.get("/company/me"), api.get("/company/customers")]);
    setCompany(c.data);
    setCustomers(cu.data);
  }, []);

  const origin = window.location.origin;

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
    loadRequests().catch(() => {});
    api
      .get("/plans")
      .then(({ data }) => setPlans(data))
      .catch(() => {});
  }, [load, loadRequests]);

  const reloadRefs = async (id) => {
    try {
      const { data } = await api.get(`/company/customers/${id}/references`);
      setRefs((r) => ({ ...r, [id]: data }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggle = async (id) => {
    if (expanded === id) return setExpanded(null);
    setExpanded(id);
    if (!refs[id]) await reloadRefs(id);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/company/customers", {
        ...form,
        origin_url: window.location.origin,
      });
      toast[data.sent ? "success" : "warning"](
        data.sent
          ? `Customer created and invite emailed. Password: ${data.temp_password}`
          : `Customer created, but the invite email failed: ${data.error}. Share this password with them: ${data.temp_password}`,
        { duration: data.sent ? 5000 : 14000 }
      );
      setOpen(false);
      setLookup(null);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", federal_tax_id: "" });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "profile_exists") {
        setLookup({
          found: true,
          matched_on: detail.matched_on,
          notice: detail.message,
          already_yours: false,
          request_status: null,
        });
        toast.info(detail.message, { duration: 9000 });
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const searchNetwork = async (query) => {
    setSearching(true);
    try {
      const { data } = await api.post("/company/customers/lookup", query);
      setLookup(data);
      return data;
    } catch (err) {
      toast.error(errMsg(err));
      return null;
    } finally {
      setSearching(false);
    }
  };

  const sendAccessRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/access-requests", {
        federal_tax_id: form.federal_tax_id,
        email: form.email,
        origin_url: window.location.origin,
      });
      toast.success(
        data.sent
          ? `Access request sent to ${data.email}. They approve it from their portal.`
          : `Access request created, but the email failed: ${data.error}`,
        { duration: data.sent ? 6000 : 12000 }
      );
      setOpen(false);
      setLookup(null);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", federal_tax_id: "" });
      await loadRequests();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadW9 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/company/w9", fd);
      toast.success("W-9 uploaded");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const openW9 = async () => {
    try {
      const { data } = await api.get("/w9?scope=own", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) {
      toast.error("Unable to open the W-9");
    }
  };

  const removeCustomer = async (c) => {
    if (
      !window.confirm(
        `Remove ${c.company_name}? Their login and all their trade references will be deleted. This cannot be undone.`
      )
    )
      return;
    try {
      const { data } = await api.delete(`/company/customers/${c.id}`);
      toast.success(
        `${c.company_name} removed${data.references_removed ? ` (${data.references_removed} reference(s) deleted)` : ""}`
      );
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resendInvite = async (c) => {
    try {
      const { data } = await api.post(
        `/company/customers/${c.id}/invite?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast[data.sent ? "success" : "warning"](
        data.sent
          ? `Invite emailed to ${c.email}`
          : `Invite could not be emailed: ${data.error}`,
        { duration: data.sent ? 5000 : 12000 }
      );
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const downloadPacket = async (c) => {
    try {
      const { data } = await api.get(`/company/customers/${c.id}/packet`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Credit Packet - ${c.company_name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Application packet downloaded");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const field = (key, label, type = "text", required = true) => (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </Label>
      <Input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) =>
          setForm({ ...form, [key]: type === "tel" ? formatPhone(e.target.value) : e.target.value })
        }
        data-testid={`customer-${key}-input`}
        className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
      />
    </div>
  );

  const archiveCustomer = async (c) => {
    if (
      !window.confirm(
        `Archive ${c.company_name}? We save their complete credit application as a PDF in your Archive and mark the application inactive. The customer stays in ConfirmTerms.`
      )
    )
      return;
    try {
      await api.post(`/company/customers/${c.id}/archive`);
      toast.success(`${c.company_name} archived — find the PDF in your Archive tab`);
      setArchiveKey((k) => k + 1);
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? customers.filter((c) =>
            [c.company_name, c.contact_name, c.email, c.phone].some((v) =>
              (v || "").toLowerCase().includes(q)
            )
          )
        : customers,
    [customers, q]
  );

  return (
    <Shell
      title={company?.name || "Vendor/Supplier workspace"}
      portalLabel="Vendor/Supplier Portal"
      themeName="navy"
      roleLine={
        isAdmin
          ? "You sell products or services and use ConfirmTerms to evaluate new customers before extending credit."
          : "You have a team login on this workspace — track credit applications and reference results in real time."
      }
      actions={
        tab === "customers" ? (
        <div className="flex items-center gap-3">
        <NotificationBell onOpened={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-customer-button"
              className="h-11 rounded-full bg-blue-600 px-6 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
                Set up a new customer
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5" data-testid="customer-form">
              <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-5">
                <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Federal Tax ID
                </Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={form.federal_tax_id}
                    placeholder="12-3456789"
                    onChange={(e) => {
                      setForm({ ...form, federal_tax_id: e.target.value });
                      setLookup(null);
                    }}
                    data-testid="customer-federal_tax_id-input"
                    className="h-11 border-slate-300 focus-visible:ring-blue-600"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!form.federal_tax_id || searching}
                    onClick={() => searchNetwork({ federal_tax_id: form.federal_tax_id })}
                    data-testid="search-network-button"
                    className="h-11 shrink-0 rounded-full border-slate-300 px-5 font-semibold"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    {searching ? "Searching…" : "Search network"}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Optional. If this business already has a credit profile in ConfirmTerms you can
                  request access instead of asking them to fill the form again.
                </p>
                {lookup && !lookup.found && (
                  <p
                    className="mt-3 text-sm font-medium text-slate-600"
                    data-testid="network-not-found"
                  >
                    No profile found in the network — continue with the details below.
                  </p>
                )}
                {lookup?.found && (
                  <div
                    className="mt-4 rounded-lg border border-blue-300 bg-blue-50 p-4"
                    data-testid="network-found-notice"
                  >
                    <p className="text-sm font-semibold text-blue-900">{lookup.notice}</p>
                    {lookup.already_yours ? (
                      <p className="mt-2 text-sm text-blue-800">
                        This profile is already one of your customers.
                      </p>
                    ) : lookup.request_status ? (
                      <p className="mt-2 text-sm text-blue-800" data-testid="network-request-status">
                        An access request is already {lookup.request_status} for this profile.
                      </p>
                    ) : (
                      <Button
                        type="button"
                        disabled={busy}
                        onClick={sendAccessRequest}
                        data-testid="send-access-request-button"
                        className="mt-3 h-10 rounded-full bg-blue-600 px-5 font-semibold hover:bg-blue-700"
                      >
                        <Send className="mr-2 h-4 w-4" /> Send access request
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {field("company_name", "Customer company name")}
              {field("contact_name", "Contact name")}
              {field("email", "Email address", "email")}
              {field("phone", "Phone number", "tel", false)}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={busy || (lookup?.found && !lookup.already_yours)}
                  data-testid="customer-submit-button"
                  className="h-11 w-full rounded-md bg-blue-600 font-semibold hover:bg-blue-700"
                >
                  {busy ? "Creating…" : "Create customer login"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
        ) : null
      }
    >
      <div className="mb-8 flex gap-2 border-b border-slate-200">
        {[
          ["customers", "Customers"],
          ["archive", "Archive"],
          ["users", "Users"],
          ["profile", "Vendor/Supplier profile"],
        ]
          .filter(([key]) => isAdmin || key === "customers" || key === "archive" || key === "users")
          .map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`-mb-px border-b-2 px-4 pb-3 pt-1 text-sm font-bold tracking-tight transition-colors duration-200 ${
              tab === key
                ? "border-blue-600 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "profile" ? (
        <CompanyProfile plans={plans} onChanged={load} />
      ) : tab === "users" ? (
        <TeamUsersTab isAdmin={isAdmin} onUpgrade={() => setTab("profile")} />
      ) : tab === "archive" ? (
        <ArchiveTab key={archiveKey} company={company} onChanged={load} />
      ) : (
        <>
      <UsageMeter refreshKey={archiveKey} onUpgrade={() => setTab("profile")} />

      {requests.length > 0 && (
        <div
          className="mb-10 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="access-requests-panel"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Network access requests
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Profiles already in the ConfirmTerms network. Nothing is shared with you until the
            customer approves from their portal.
          </p>
          <div className="mt-5 divide-y divide-slate-100">
            {requests.map((r) => (
              <div
                key={r.id}
                data-testid={`access-request-${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="text-sm text-slate-700">
                  <span className="font-semibold text-slate-900">{r.email}</span>
                  {r.federal_tax_id ? ` · Tax ID ${r.federal_tax_id}` : ""}
                  <span className="text-slate-400">
                    {" "}
                    · matched on {r.matched_on === "tax_id" ? "Federal Tax ID" : "email"}
                  </span>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    r.status === "approved"
                      ? "bg-emerald-50 text-emerald-700"
                      : r.status === "declined"
                        ? "bg-red-50 text-red-600"
                        : "bg-amber-50 text-amber-700"
                  }`}
                  data-testid={`access-request-status-${r.id}`}
                >
                  {r.status === "pending" ? "Awaiting customer approval" : r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-10 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Vendor/Supplier W-9
            </div>
            <p className="mt-2 text-sm text-slate-600" data-testid="w9-status">
              {company?.has_w9
                ? `${company.w9_filename} is available to all of your customers.`
                : "No W-9 uploaded yet. Upload a PDF so customers can view or download it."}
            </p>
          </div>
          <div className="flex gap-3">
            {company?.has_w9 && (
              <Button
                variant="outline"
                onClick={openW9}
                data-testid="view-w9-button"
                className="h-11 rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
              >
                <Download className="mr-2 h-4 w-4" /> View W-9
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={uploadW9}
              className="hidden"
              data-testid="w9-file-input"
            />
            {isAdmin && (
            <Button
              onClick={() => fileRef.current?.click()}
              data-testid="upload-w9-button"
              className="h-11 rounded-full bg-slate-900 px-6 font-semibold transition-colors duration-200 hover:bg-slate-800 active:scale-95"
            >
              <Upload className="mr-2 h-4 w-4" /> {company?.has_w9 ? "Replace" : "Upload"} W-9
            </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <h2
          className="font-display text-xs font-bold uppercase tracking-[0.22em] text-slate-500"
          data-testid="customers-heading"
        >
          Customers
        </h2>
        <div className="flex items-center gap-4">
          {customers.length > 0 && (
            <div className="relative w-64">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search customers…"
                data-testid="customer-search-input"
                className="h-10 border-slate-300 pl-10 focus-visible:ring-blue-600"
              />
            </div>
          )}
          <span className="text-xs font-semibold text-slate-400">
            {customers.length} total
          </span>
        </div>
      </div>

      {customers.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center"
          data-testid="customers-empty"
        >
          <p className="font-display text-lg font-bold text-slate-900">No customers yet</p>
          <p className="mt-2 text-sm text-slate-500">
            Add a customer to generate their ConfirmTerms login.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="customers-list">
          {filtered.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500"
              data-testid="customer-search-no-results"
            >
              No customers match &ldquo;{query}&rdquo;.
            </div>
          )}
          {filtered.map((c) => (
            <div
              key={c.id}
              className="rounded-lg border border-slate-200 bg-white shadow-sm"
              data-testid={`customer-row-${c.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4 p-6">
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-slate-900">
                    {c.company_name}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {c.contact_name} · {c.email}
                    {c.phone ? ` · ${formatPhone(c.phone)}` : ""}
                  </p>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {c.references_completed}/{c.references_total} references returned
                  </span>
                  <EditDialog
                    testId={`edit-customer-${c.id}`}
                    title="Edit customer profile"
                    endpoint={`/company/customers/${c.id}`}
                    onSaved={load}
                    fields={[
                      ["company_name", "Customer company name"],
                      ["contact_name", "Contact name"],
                      ["email", "Email address", "email"],
                      ["phone", "Phone number", "tel", false],
                    ]}
                    initial={{
                      company_name: c.company_name,
                      contact_name: c.contact_name,
                      email: c.email,
                      phone: c.phone || "",
                    }}
                  />
                  <ResetPasswordButton
                    testId={`reset-customer-${c.id}`}
                    label={`${c.email}`}
                    endpoint={`/company/customers/${c.id}/reset-password`}
                    onDone={load}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resendInvite(c)}
                    data-testid={`resend-invite-${c.id}`}
                    className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                  >
                    <Mail className="mr-1.5 h-3.5 w-3.5" /> Resend invite
                  </Button>
                  <UpgradeToCompanyButton
                    testId={`upgrade-customer-${c.id}`}
                    customerId={c.id}
                    customerName={c.company_name}
                    onDone={load}
                  />
                  <CreditApplicationView customerId={c.id} customerName={c.company_name} />
                  {c.active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => archiveCustomer(c)}
                      data-testid={`archive-customer-${c.id}`}
                      className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                    >
                      <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                    </Button>
                  ) : (
                    <span
                      data-testid={`inactive-badge-${c.id}`}
                      className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
                    >
                      Inactive · archived
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPacket(c)}
                    data-testid={`download-packet-${c.id}`}
                    className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                  >
                    <FileDown className="mr-1.5 h-3.5 w-3.5" /> Packet PDF
                  </Button>
                  {isAdmin && (
                  <button
                    onClick={() => removeCustomer(c)}
                    data-testid={`delete-customer-${c.id}`}
                    className="rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                    aria-label={`Remove ${c.company_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggle(c.id)}
                    data-testid={`toggle-references-${c.id}`}
                    className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                  >
                    References
                    <ChevronDown
                      className={`ml-1.5 h-3.5 w-3.5 transition-transform duration-200 ${
                        expanded === c.id ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                </div>
              </div>

              {c.temp_password && (
                <div className="grid gap-4 border-t border-slate-100 px-6 py-5 sm:grid-cols-2">
                  <CopyField label="Customer login" value={c.email} testId={`customer-${c.id}-email`} />
                  <CopyField
                    label="Generated password"
                    value={c.temp_password}
                    testId={`customer-${c.id}-password`}
                  />
                  {c.setup_link && (
                    <div className="sm:col-span-2">
                      <CopyField
                        label="Password setup link (share if the email did not arrive)"
                        value={`${origin}${c.setup_link}`}
                        testId={`customer-${c.id}-setup-link`}
                      />
                    </div>
                  )}
                </div>
              )}

              {expanded === c.id && (
                <div className="border-t border-slate-100 bg-slate-50 p-6" data-testid={`references-panel-${c.id}`}>
                  {(refs[c.id] || []).length === 0 ? (
                    <p className="text-sm text-slate-500">
                      This customer has not added any references yet.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {refs[c.id].map((r) => (
                        <div
                          key={r.id}
                          className="rounded-md border border-slate-200 bg-white p-5"
                          data-testid={`reference-card-${r.id}`}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <div className="font-display text-base font-bold text-slate-900">
                                {r.company_name}
                              </div>
                              <div className="text-sm text-slate-600">
                                {r.contact_name} · {r.email}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  r.status === "completed"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {r.status === "completed" ? "Completed" : "Awaiting response"}
                              </span>
                              <EditDialog
                                testId={`edit-reference-${r.id}`}
                                title="Edit reference profile"
                                endpoint={`/references/${r.id}`}
                                onSaved={() => reloadRefs(c.id)}
                                fields={[
                                  ["company_name", "Reference company name"],
                                  ["contact_name", "Contact name"],
                                  ["email", "Contact email", "email"],
                                ]}
                                initial={{
                                  company_name: r.company_name,
                                  contact_name: r.contact_name,
                                  email: r.email,
                                }}
                              />
                            </div>
                          </div>
                          {r.responses && (
                            <dl className="mt-4 grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
                              {QUESTIONS.filter(([k]) => r.responses[k]).map(([k, label]) => (
                                <div key={k}>
                                  <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                                    {label}
                                  </dt>
                                  <dd className="mt-1 text-sm text-slate-900">
                                    {k === "phone" ? formatPhone(r.responses[k]) : r.responses[k]}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </>
      )}
    </Shell>
  );
}
